import { useCallback, useMemo, useState } from 'react'
import { FileEdit, Quote, Trash2, UserSearch, X } from 'lucide-react'

import { MarkdownDisplay, UserDisplay } from '@/components'
import { Copy02 } from '@/components/Icons'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { useActionableCommentStore } from '@/features/actionable-comment'
import { useMinute } from '@/hooks/useMinute.ts'
import { ResponseComment, ResponseThread, ThreadActionableType } from '@/schema'
import { cn, formatFullName } from '@/utils'
import { getUserInitials } from '@/utils/avatar'

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '../../../../components/ui'
import { extractMentionedUserIds } from '../../utils/mentionUtils'
import { ExpandableCommentText } from '../CommentSidebar/ExpandableCommentText'

import { useGetUserCommentData } from './hooks/useGetUserCommentData'
import { EditCommentForm } from './EditCommentForm'

interface ThreadCommentItemProps {
  comment: ResponseThread | ResponseComment
  isRoot?: boolean
  isEditing: boolean
  isCopied?: boolean
  isViewer: boolean
  onCopy: () => void
  onEdit: () => void
  onDelete: () => void
  onReply?: () => void
  onCancelEdit: () => void
  onSubmitEdit: (newBody: string) => void
  onQuoteComment: () => void
  isPending?: boolean
  threadStatus: string
  isLatestVersion?: boolean
  commentRoot?: ResponseThread
  quotedComment?: ResponseComment
}

export const ThreadCommentItem: React.FC<ThreadCommentItemProps> = ({
  comment,
  isRoot,
  isEditing,
  onCopy,
  onEdit,
  onDelete,
  onCancelEdit,
  onSubmitEdit,
  onQuoteComment,
  isPending,
  threadStatus,
  isLatestVersion = true,
  commentRoot,
  quotedComment,
  isViewer,
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false)

  const now = useMinute()
  const {
    relativeTime,
    isAuthor,
    contentText,
    isEdited,
    isEditable,
    isDeletable,
    isQuotable,
  } = useGetUserCommentData(comment, threadStatus || '', now)

  const { setActionableDialogOpen, setNotActionableDialogOpen } =
    useActionableCommentStore()

  const handleOpenChange = (open: boolean) => {
    setIsMenuOpen(open)
  }

  const handleMarkAsActionable = () => {
    if (!commentRoot?.thread_id) return

    const mentionedUserIds = extractMentionedUserIds(commentRoot.content)
    const assigneeId = mentionedUserIds?.[0] || null

    setActionableDialogOpen({
      open: true,
      threadId: commentRoot.thread_id,
      assigneeId,
    })
  }

  const handleChangeToActionable = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation()

      if (!commentRoot?.thread_id) return

      const mentionedUserIds = extractMentionedUserIds(commentRoot.content)
      const assigneeId = mentionedUserIds?.[0] || null

      setActionableDialogOpen({
        open: true,
        threadId: commentRoot.thread_id,
        assigneeId,
        isChangeToActionable: true,
      })
    },
    [commentRoot?.content, commentRoot?.thread_id, setActionableDialogOpen]
  )

  const handleMarkAsNotActionable = () => {
    if (!commentRoot?.thread_id) return
    setNotActionableDialogOpen(true, commentRoot.thread_id)
  }

  const isAllowActionable = useMemo(
    () => commentRoot?.actionable_type === ThreadActionableType.NONE,
    [commentRoot?.actionable_type]
  )

  const isAllowChangeToActionable = useMemo(
    () => commentRoot?.actionable_type === ThreadActionableType.NOT_ACTIONABLE,
    [commentRoot?.actionable_type]
  )

  return (
    <div id={comment.id} className="group relative flex gap-3 px-4 py-3">
      <div className="relative flex-shrink-0">
        <UserDisplay
          user={comment.created_by_user}
          showName={false}
          showAvatar
          avatarSize="h-10 w-10 border border-solid border-white"
          className="block"
        />
        {isAuthor && (
          <div
            className="absolute"
            style={{
              top: '30px',
              left: '30px',
              width: '12px',
              height: '12px',
              borderRadius: 'var(--radius-full)',
              border: '1.5px solid var(--Colors-Background-bg-primary)',
              backgroundColor: 'var(--Colors-Foreground-fg-success-secondary)',
            }}
          />
        )}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-baseline justify-between gap-2">
          <span>
            {isAuthor ? (
              'Me'
            ) : (
              <UserDisplay
                user={comment.created_by_user}
                showAvatar={false}
                textClassName="text-secondary-700 font-medium text-ellipsis overflow-hidden text-sm"
              />
            )}
          </span>
          <span className="text-xs text-gray-500 flex-shrink-0">
            {isEdited ? `${relativeTime} • Edited` : relativeTime}
          </span>
        </div>

        {isEditing ? (
          <EditCommentForm
            initialValue={comment.content}
            onCancel={onCancelEdit}
            onSubmit={onSubmitEdit}
            isPending={isPending}
          />
        ) : (
          <>
            <div className="mt-1.5 rounded-sm bg-primary text-md text-primary-900 break-words border border-solid border-secondary rounded-tl-none overflow-hidden">
              {quotedComment && (
                <div className="px-1 py-1 pb-3 bg-gray-100 border border-solid border-white rounded-sm rounded-tl-none flex flex-col gap-2">
                  <div className="flex gap-1 items-center">
                    <Quote className="size-3" />
                    <Avatar className="size-6 border border-solid border-white">
                      <AvatarImage
                        src={quotedComment.created_by_user?.avatar_url ?? ''}
                        alt={formatFullName(
                          quotedComment.created_by_user?.first_name ?? '',
                          quotedComment.created_by_user?.last_name ?? ''
                        )}
                      />
                      <AvatarFallback className="text-xs bg-white">
                        {quotedComment.created_by_user
                          ? getUserInitials(quotedComment.created_by_user)
                          : ''}
                      </AvatarFallback>
                    </Avatar>
                    <div className="text-sm font-medium">
                      {formatFullName(
                        quotedComment.created_by_user?.first_name ?? '',
                        quotedComment.created_by_user?.last_name ?? ''
                      )}
                    </div>
                  </div>
                  <ExpandableCommentText
                    content={quotedComment.content}
                    isAllowExpandable={false}
                  />
                </div>
              )}
              <MarkdownDisplay content={contentText} className="px-3 py-2" />
            </div>
            {isLatestVersion && !isViewer && (
              <div
                className={cn(
                  'absolute right-6 -bottom-2',
                  'opacity-0 transition-opacity group-hover:opacity-100',
                  'pointer-events-none z-10',
                  isMenuOpen && 'opacity-100'
                )}
              >
                <div
                  className={cn(
                    'flex items-center gap-1.5',
                    'rounded-md bg-gray-dark-900 px-md py-sm shadow-xl',
                    'pointer-events-auto' // buttons are clickable
                  )}
                >
                  {isQuotable ? (
                    <Tooltip delayDuration={300}>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          onClick={onQuoteComment}
                          className="size-5 flex items-center justify-center p-0 border-none bg-transparent cursor-pointer"
                          aria-label="Quote comment"
                        >
                          <Quote className="size-4 text-foreground-fg-quaternary-400 hover:text-white" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <span>Quote</span>
                      </TooltipContent>
                    </Tooltip>
                  ) : null}

                  {isRoot && (
                    <>
                      {isAllowActionable ? (
                        <DropdownMenu onOpenChange={handleOpenChange}>
                          <Tooltip delayDuration={300}>
                            <TooltipTrigger asChild>
                              <DropdownMenuTrigger asChild>
                                <button
                                  type="button"
                                  className="size-5 flex items-center justify-center p-0 border-none bg-transparent cursor-pointer"
                                  aria-label="Not review"
                                >
                                  <UserSearch className="size-4 text-foreground-fg-quaternary-400 hover:text-white" />
                                </button>
                              </DropdownMenuTrigger>
                            </TooltipTrigger>
                            <TooltipContent>Not review</TooltipContent>
                          </Tooltip>
                          <DropdownMenuContent
                            align="end"
                            className="sm:w-[130px] sm:min-w-none"
                          >
                            <>
                              <DropdownMenuItem
                                onClick={handleMarkAsActionable}
                                className="text-sm text-primary-900 font-medium pl-4 hover:bg-gray-100 px-2 py-2"
                              >
                                Actionable
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={handleMarkAsNotActionable}
                                className="text-sm text-primary-900 font-medium pl-4 hover:bg-gray-100 px-2 py-2"
                              >
                                Not actionable
                              </DropdownMenuItem>
                            </>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      ) : null}
                      {isAllowChangeToActionable ? (
                        <Tooltip delayDuration={300}>
                          <TooltipTrigger asChild>
                            <button
                              type="button"
                              className="size-5 flex items-center justify-center p-0 border-none bg-transparent cursor-pointer"
                              aria-label="Actionable"
                              onClick={handleChangeToActionable}
                            >
                              <X className="size-4 text-foreground-fg-quaternary-400 hover:text-white" />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <span>Actionable</span>
                          </TooltipContent>
                        </Tooltip>
                      ) : null}
                    </>
                  )}

                  <Tooltip delayDuration={300}>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        onClick={onCopy}
                        className="size-5 flex items-center justify-center p-0 border-none bg-transparent cursor-pointer"
                        aria-label="Copy comment"
                      >
                        <Copy02 className="size-4 text-foreground-fg-quaternary-400 hover:text-white" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <span>Copy</span>
                    </TooltipContent>
                  </Tooltip>

                  {isAuthor && (
                    <>
                      {isEditable && (
                        <Tooltip delayDuration={300}>
                          <TooltipTrigger asChild>
                            <button
                              type="button"
                              onClick={onEdit}
                              className="size-5 flex items-center justify-center p-0 border-none bg-transparent cursor-pointer"
                              aria-label="Edit comment"
                            >
                              <FileEdit className="size-4 text-foreground-fg-quaternary-400 hover:text-white" />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <span>Edit</span>
                          </TooltipContent>
                        </Tooltip>
                      )}

                      {isDeletable && (
                        <Tooltip delayDuration={300}>
                          <TooltipTrigger asChild>
                            <button
                              type="button"
                              onClick={onDelete}
                              className="size-5 flex items-center justify-center p-0 border-none bg-transparent cursor-pointer"
                              aria-label="Delete comment"
                            >
                              <Trash2 className="size-4 text-foreground-fg-quaternary-400 hover:text-white" />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <span>Delete</span>
                          </TooltipContent>
                        </Tooltip>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
